import { useCallback, useEffect, useState } from 'react'
import { hasPush } from '@/config'
import { pushSupported, isStandalone, isIOS } from '@/lib/push/install'
import { enablePush, disablePush, getExistingSubscription } from '@/lib/push/client'
import { useLocationStore } from '@/store/location'

export type PushStatus =
  | 'loading'
  | 'unsupported' // browser lacks Push/Notification API
  | 'unconfigured' // no VAPID key wired yet
  | 'needs-install' // iOS, not added to Home Screen → push impossible until installed
  | 'denied' // user blocked notifications
  | 'available' // ready to enable
  | 'subscribed' // active subscription

export function usePush() {
  const [status, setStatus] = useState<PushStatus>('loading')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const saved = useLocationStore((s) => s.saved)

  const refresh = useCallback(async () => {
    if (!pushSupported()) return setStatus('unsupported')
    if (!hasPush) return setStatus('unconfigured')
    if (isIOS() && !isStandalone()) return setStatus('needs-install')
    if (Notification.permission === 'denied') return setStatus('denied')
    const sub = await getExistingSubscription()
    setStatus(sub ? 'subscribed' : 'available')
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const enable = useCallback(async () => {
    if (!saved) {
      setError('Set your location first.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await enablePush(saved)
      setStatus('subscribed')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not enable alerts.')
      await refresh()
    } finally {
      setBusy(false)
    }
  }, [saved, refresh])

  const disable = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      await disablePush()
      setStatus('available')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not turn off alerts.')
    } finally {
      setBusy(false)
    }
  }, [])

  return { status, busy, error, enable, disable, refresh, isIOS: isIOS() }
}
